import { useCallback, useEffect } from 'react'
import {
  makeVar,
  useMutation,
  useQuery,
  useReactiveVar,
  useSubscription
} from '@apollo/client'
import { DateTime, Duration, Interval } from 'luxon'
import { useIdleTimer } from 'react-idle-timer'

import {
  courseProgressionQuery,
  matchesQuery,
  matchesStatusQuery,
  newPendingMatchSubscription,
  pageSize,
  progressionPageSize,
  triggerAutomatchMutation,
  userQuery
} from 'src/graphql'
import type {
  CourseProgressionQuery,
  CourseProgressionQueryVariables,
  MatchFieldsFragment as Match,
  MatchProgressionFieldsFragment,
  MatchesQuery,
  MatchesQueryVariables,
  MatchesStatusQuery,
  MatchesStatusQueryVariables,
  NewPendingMatchSubscription,
  NewPendingMatchSubscriptionVariables,
  PendingMatchFieldsFragment as PendingMatch,
  TriggerAutomatchMutation,
  TriggerAutomatchMutationVariables
} from 'src/graphql/types'
import { sortByDate } from 'src/lib/utils/date'
import { pageToEdges } from 'src/lib/utils/transforms'
import { AnalyticsEvent, useEventAnalytics } from 'src/hooks/use-analytics'

const automatchDuration = Duration.fromObject({ hours: 2 })
const pollInterval = Duration.fromObject({ seconds: 30 }).as('milliseconds')
const togglePollingInterval = Duration.fromObject({ seconds: 5 }).as(
  'milliseconds'
)
const idleTimeout = Duration.fromObject({ minutes: 30 }).as('milliseconds')

// How many matches the list currently shows. Shared across every useMatches()
// consumer so all observers watch the same matches({first:N}) cache entry, and
// grows by a page on "Show more". Paginating by a growing `first` (rather than a
// cursor) means the frequent background refetches — the 30s pending-match poll,
// the pending-match subscription, the new-match check — re-pull the whole visible
// window instead of resetting it to the first page and dropping the extra pages.
const visibleMatchCount = makeVar(pageSize)

export function useMatches() {
  const first = useReactiveVar(visibleMatchCount)

  const { data, previousData, error, loading, refetch } = useQuery<
    MatchesQuery,
    MatchesQueryVariables
  >(matchesQuery, {
    variables: { pageSize: first },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true
  })

  // Keep the current window on screen while a larger one loads so growing the
  // list never flashes empty.
  const source = data ?? previousData

  const hasMoreMatches = source?.matches.pageInfo.hasNextPage ?? false

  const matches =
    (pageToEdges(source?.matches) as (Match | PendingMatch)[]) ?? []

  const fetchMore = () => {
    visibleMatchCount(visibleMatchCount() + pageSize)
  }

  return {
    refetch,
    loading,
    error,
    matches: sortByDate(['date', 'createdAt'], matches),
    hasMoreMatches,
    fetchMore
  }
}
export function useTriggerAutomatch() {
  const automatchTriggeredEvent = useEventAnalytics(
    AnalyticsEvent.automatchTriggered
  )
  const automatchNotTriggeredEvent = useEventAnalytics(
    AnalyticsEvent.automatchNotTriggered
  )

  const { data, refetch, startPolling, stopPolling } = useQuery<
    MatchesStatusQuery,
    MatchesStatusQueryVariables
  >(matchesStatusQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network'
  })

  const linkId = data?.user?.steamId

  const [triggerAutomatch] = useMutation<
    TriggerAutomatchMutation,
    TriggerAutomatchMutationVariables
  >(triggerAutomatchMutation, {
    refetchQueries: [{ query: userQuery }, { query: matchesStatusQuery }]
  })

  const trigger = useCallback(
    async (reason: string) => {
      if (!linkId) return
      try {
        await triggerAutomatch({ variables: { linkId, reason } })
        automatchTriggeredEvent({ automatch_trigger_reason: reason })
      } catch (err: any) {
        automatchNotTriggeredEvent({
          error: err?.message,
          automatch_trigger_reason: reason
        })
      }
    },
    [
      automatchTriggeredEvent,
      automatchNotTriggeredEvent,
      triggerAutomatch,
      linkId
    ]
  )

  return {
    linkId,
    data,
    refetch,
    startPolling,
    stopPolling,
    trigger
  }
}

export function useMatchesStatus() {
  const automatchSkippedTriggerEvent = useEventAnalytics(
    AnalyticsEvent.automatchSkippedTrigger
  )

  const { linkId, data, refetch, startPolling, stopPolling, trigger } =
    useTriggerAutomatch()

  const automatch = data?.user?.automatch
  const matchEdges = data?.latestMatches?.edges
  const latestMatchDate = matchEdges?.find((m) => !!m)?.node?.createdAt

  useSubscription<
    NewPendingMatchSubscription,
    NewPendingMatchSubscriptionVariables
  >(newPendingMatchSubscription, {
    onSubscriptionData: () => {
      refetch()
    }
  })

  useEffect(() => {
    const togglePolling = () => {
      const checkedAt = data?.user?.automatch?.checkedAt

      if (checkedAt) {
        const now = DateTime.local()

        const duration = Interval.fromDateTimes(
          DateTime.fromISO(checkedAt),
          now
        ).toDuration()

        if (duration.isValid) {
          duration > automatchDuration
            ? startPolling(pollInterval)
            : stopPolling()
        }
      }
    }

    const interval = globalThis.setInterval(
      togglePolling,
      togglePollingInterval
    )

    return () => {
      stopPolling()
      globalThis.clearInterval(interval)
    }
  })

  const skipTrigger = useCallback(
    (reason: string) => {
      automatchSkippedTriggerEvent({ automatch_skip_trigger_reason: reason })
    },
    [automatchSkippedTriggerEvent]
  )

  const handleActivity = () => {
    if (!linkId || !automatch) return skipTrigger('unlinked')
    if (!automatch.triggeredAt) return trigger('never_triggered')

    const triggeredAt = DateTime.fromISO(automatch.triggeredAt)
    if (triggeredAt.diffNow('minutes').minutes > -5)
      return skipTrigger('rate_limit')

    if (!latestMatchDate) return trigger('no_matches')
    if (!automatch.checkedAt) return trigger('never_checked')

    const matchDate = DateTime.fromISO(latestMatchDate)
    if (Math.abs(matchDate.diffNow('minutes').minutes) > 30) {
      return trigger('old_match')
    } else {
      return skipTrigger('recent_match')
    }
  }

  useIdleTimer({
    timeout: idleTimeout,
    crossTab: true,
    onActive: handleActivity,
    onAction: handleActivity,
    debounce: 5000
  })

  return { data }
}

export function useCourseProgression() {
  const { data, loading, refetch } = useQuery<
    CourseProgressionQuery,
    CourseProgressionQueryVariables
  >(courseProgressionQuery, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    variables: { pageSize: progressionPageSize }
  })
  const edges = data?.progression?.edges
  const matches =
    edges &&
    (edges
      .filter((edge: any) => !!edge)
      .map((edge: any) => edge?.node)
      .filter((node: any) => !!node) as MatchProgressionFieldsFragment[])
  return { matches, loading, refetch }
}
